import { applyBarrier, concurrency, createQuery as createFarfetchedQuery } from '@farfetched/core';
import { createInstance } from '@leprecoins/effector-query';
import { OverloadedParameters } from '@leprecoins/utility-types';
import { attach, sample } from 'effector';
import qs from 'qs';
import { API_URL } from 'shared/config';
import { applyNotifications, notifications, translated } from 'shared/lib';
import { authModel } from './auth-model';

const [createBaseAuthQuery, createBaseAuthMutation] = createInstance({
	url: API_URL,
	credentials: 'include',
	headers: authModel.$token.map((token) => ({ Authorization: `Bearer ${token}` })),
});

export const createAuthQuery: typeof createBaseAuthQuery = (
	params: OverloadedParameters<typeof createBaseAuthQuery>[0],
) => {
	const query = createBaseAuthQuery(params);

	concurrency(query, { strategy: 'TAKE_LATEST' });

	applyBarrier(query, { barrier: authModel.authBarrier });

	applyNotifications(query);

	return query;
};

export const createAuthMutation: typeof createBaseAuthMutation = (
	params: OverloadedParameters<typeof createBaseAuthMutation>[0],
) => {
	const mutation = createBaseAuthMutation(params);

	applyBarrier(mutation, { barrier: authModel.authBarrier });

	applyNotifications(mutation);

	return mutation;
};

export type CreateFileDownloadQueryParams<TParams> = {
	url: string;
	fileName: (params: TParams) => string;
};

export const createAuthFileDownloadQuery = <TParams>({
	url,
	fileName,
}: CreateFileDownloadQueryParams<TParams>) => {
	const query = createFarfetchedQuery({
		handler: attach({
			source: authModel.$token,
			effect: async (token, ps: TParams) => {
				const stringified = qs.stringify(ps, { arrayFormat: 'repeat' });

				const response = await fetch(`${API_URL}${url}?${stringified}`, {
					credentials: 'include',
					headers: { Authorization: `Bearer ${token}` },
				});

				const blob = await response.blob();
				const createdUrl = window.URL.createObjectURL(blob);
				const link = document.createElement('a');

				link.href = createdUrl;
				link.download = fileName(ps);
				link.click();
				window.URL.revokeObjectURL(createdUrl);
			},
		}),
	});

	applyBarrier(query, { barrier: authModel.authBarrier });

	sample({
		clock: query.finished.failure,
		source: translated('errors:client-messages.failed-to-download'),
		fn: (message) => ({ message }),
		target: notifications.showError,
	});

	return query;
};
